import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../../../app/hooks';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { StatusDropdownOption } from '../../../../common/_custom/StatusDropdown/StatusDropdown';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';
import { NEUTRAL_CHIP, NO_GRADE_OPTIONS, PROFILE_GRADE_FIELD } from './constants';
import type { GradeOptions, GradeOptionsByCategory } from './types';
import type { OrgBasedUser, OrgBasedUsersTableProps } from '../OrgBasedUsersTable/OrgBasedUsersTable';

const findGradeOptions = (config?: { columns?: { field: string; dropdownOptions?: GradeOptions }[] }): GradeOptions =>
    config?.columns?.find((column) => column.field === PROFILE_GRADE_FIELD)?.dropdownOptions ?? {};

/**
 * Supplies the lender/partner card's chip row: Organization Type, Owner and Profile Grade.
 *
 * Owner ids are resolved against the `ownerDropdownOptions` store — the API returns
 * userIds, never labels (RCPI-10910). That store is filled by the route wrapper's own
 * guarded fetch, so nothing here requests owners. Profile Grade colours come from the
 * per-PNO ViewConfig for the category, so a PNO without that column simply has no chip.
 */
export const useOrgCardChips = (category: OrgBasedUsersTableProps['category']) => {
    const userData = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const owners = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const [gradeOptionsByCategory, setGradeOptionsByCategory] = useState<GradeOptionsByCategory>(NO_GRADE_OPTIONS);

    // One AICA_DEAL_NAVBAR_VIEW response carries both categories' columns, so both are resolved
    // from it — switching between Lenders and Partners is a lookup, not a refetch.
    // Identity and domainMetadata are blank on mount, so an unguarded fetch burns a request the API can't resolve.
    useEffect(() => {
        if (!userData?.accountType || !userData?.primaryEmail || !platformData?.investorOrgId) return;
        getDealFormConfig(
            {
                accountType: userData?.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: userData?.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (configData) =>
                    setGradeOptionsByCategory({
                        Lender: findGradeOptions(configData?.manageLendersColumns),
                        Partner: findGradeOptions(configData?.managePartnersColumns),
                    }),
                onError: () => setGradeOptionsByCategory(NO_GRADE_OPTIONS),
            },
        );
    }, [userData?.accountType, userData?.primaryEmail, platformData?.investorOrgId]);

    // Keyed once per owners change rather than scanned per row.
    const ownersById = useMemo(() => new Map(owners.map((option) => [option.userId, option])), [owners]);

    return (row: OrgBasedUser): StatusDropdownOption[] => {
        const chips: StatusDropdownOption[] = [];

        if (row.organizationType) {
            chips.push({ label: row.organizationType, value: row.organizationType, ...NEUTRAL_CHIP });
        }

        const owner = ownersById.get(row.organizationOwner);
        if (owner?.name) {
            chips.push({ label: owner.name, value: owner.userId, ...NEUTRAL_CHIP });
        }

        const grade = row.customFields?.profileGrade;
        const gradeOption = grade ? gradeOptionsByCategory[category][grade] : undefined;
        if (gradeOption) chips.push(gradeOption);

        return chips;
    };
};
